'use client';

import React, { useEffect, useRef } from 'react';

interface Story {
  name: string;
  role: string;
  location: string;
  loanAmount: string;
  loanType: string;
  previousDecision: string;
  program: string;
  outcome: string;
  quote: string;
  initials: string;
  color: string;
}

const STORIES: Story[] = [
  {
    name: 'Marcus T.',
    role: 'Restaurant Owner',
    location: 'Miami, FL',
    loanAmount: '$185,000',
    loanType: 'SBA 7(a) Loan',
    previousDecision: 'Declined by 3 banks — insufficient collateral',
    program: 'SBA 7(a) via Lenders Home network',
    outcome: 'Funded in 22 days · 6.5% rate · 10-year term',
    quote: 'Three banks said no. Lenders Home found me an SBA program I didn\'t even know existed. My restaurant is now fully renovated.',
    initials: 'MT',
    color: 'bg-sky-light text-sky-dark',
  },
  {
    name: 'Priya N.',
    role: 'Healthcare Practice Owner',
    location: 'Columbus, OH',
    loanAmount: '$75,000',
    loanType: 'Business Term Loan',
    previousDecision: 'Quoted 24% APR by online lender',
    program: 'Healthcare Business Loan Program',
    outcome: '$75K funded · 9.2% APR · 5-year term',
    quote: 'I was quoted 24% APR elsewhere. Lenders Home found me a healthcare-specific program at 9.2%. That difference saves me thousands.',
    initials: 'PN',
    color: 'bg-sage-light text-sage-dark',
  },
  {
    name: 'Tamara W.',
    role: 'Single Parent, Nurse',
    location: 'Phoenix, AZ',
    loanAmount: '$18,000',
    loanType: 'Personal Loan',
    previousDecision: 'High-interest credit card debt at 22%',
    program: 'Debt Consolidation Personal Loan',
    outcome: '$18K consolidated · 11.4% APR · monthly savings $340',
    quote: 'I was drowning in credit card debt. Lenders Home helped me consolidate everything into one manageable payment.',
    initials: 'TW',
    color: 'bg-amber-100 text-amber-700',
  },
];

export default function BorrowerStories() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    sectionRef.current?.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="stories" className="py-24 lg:py-32 bg-slate-dark overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-16 reveal">
          <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-semibold text-sky font-body uppercase tracking-wide mb-5">
            Borrower Success Stories
          </span>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
            <h2 className="font-heading font-bold text-display text-white">
              Real borrowers.<br />
              <span className="text-sky">Real funding. Real results.</span>
            </h2>
            <p className="font-body text-white/50 text-base max-w-xs lg:text-right">
              Loan types, programs, and outcomes — actual numbers from actual funded applications.
            </p>
          </div>
        </div>

        {/* Story cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {STORIES.map((story, idx) => (
            <div
              key={story.name}
              className="reveal bg-white/5 border border-white/10 rounded-4xl p-7 flex flex-col hover:bg-white/8 transition-all duration-300"
              style={{ transitionDelay: `${idx * 100}ms` }}
            >
              {/* Profile */}
              <div className="flex items-center gap-3 mb-5">
                <div
                  className={`w-11 h-11 rounded-full flex items-center justify-center font-heading font-bold text-sm shrink-0 ${story.color}`}
                >
                  {story.initials}
                </div>
                <div>
                  <p className="font-heading font-semibold text-white text-sm">{story.name}</p>
                  <p className="font-body text-xs text-white/50">{story.role} · {story.location}</p>
                </div>
              </div>

              {/* Quote */}
              <blockquote className="font-body text-white/80 text-sm leading-relaxed italic mb-6 flex-1">
                "{story.quote}"
              </blockquote>

              {/* Data grid */}
              <div className="space-y-2.5 border-t border-white/10 pt-5">
                {[
                  { label: 'Amount', value: story.loanAmount },
                  { label: 'Type', value: story.loanType },
                  { label: 'Previously', value: story.previousDecision },
                  { label: 'Program', value: story.program },
                  { label: 'Result', value: story.outcome },
                ].map((row) => (
                  <div key={row.label} className="flex gap-2">
                    <span className="text-xs font-semibold text-white/40 font-body w-20 shrink-0 pt-0.5">
                      {row.label}
                    </span>
                    <span
                      className={`text-xs font-body leading-relaxed ${
                        row.label === 'Result' ? 'text-sage font-semibold'
                          : row.label === 'Previously' ? 'text-white/40 line-through' : 'text-white/80'
                      }`}
                    >
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Aggregate stat */}
        <div className="mt-14 p-8 lg:p-10 bg-white/5 border border-white/10 rounded-4xl text-center reveal">
          <p className="font-heading font-black text-5xl lg:text-7xl text-sky tracking-tight mb-3 stat-number">
            50,000+
          </p>
          <p className="font-heading font-bold text-xl text-white mb-2">borrowers funded since 2010</p>
          <p className="font-body text-white/50 text-sm max-w-md mx-auto">
            Over $2.4 billion in loans facilitated. Average funding timeline: 48 hours. 
            Programs available for every credit profile and loan purpose.
          </p>
        </div>
      </div>
    </section>
  );
}